import { resolveInternationalMobile } from '@/lib/phone/resolveInternationalMobile';
import type { HireClippersDraft, HireClippersStep } from './hireClippersDraft';
import {
  BUDGET_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  DEMAND_TYPE_OPTIONS,
  type HireClippersOption,
  OBJECTIVE_OPTIONS,
  PLATFORM_OPTIONS,
} from './hireClippersOptions';

/** The `BrandLeadInput` fields the app sends, named as the backend reads them. */
export interface BrandLeadPayload {
  name: string;
  email: string;
  phone: string;
  company?: string;
  demand_type: string;
  objective: string;
  content_type: string[];
  platforms: string;
  budget: string;
  source: string;
}

export type BrandLeadInputError =
  | 'required'
  | 'invalidName'
  | 'invalidEmail'
  | 'invalidPhone';

export type BuildBrandLeadInputResult =
  | { ok: true; input: BrandLeadPayload }
  | { ok: false; step: HireClippersStep; error: BrandLeadInputError };

// Mirrors normalizeBrandLeadInput in
// ../autoclipper-backend/src/lib/brandLeads/createBrandLead.js.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_MIN = 2;
const NAME_MAX = 100;
const EMAIL_MAX = 255;
const COMPANY_MAX = 150;

const isOption = (options: readonly HireClippersOption[], value: string) =>
  options.some((option) => option.value === value);

const fail = (
  step: HireClippersStep,
  error: BrandLeadInputError,
): BuildBrandLeadInputResult => ({ ok: false, step, error });

/**
 * Validates the draft in form order and builds the `createBrandLead` input.
 * The first failure names the step to send the user back to.
 */
export function buildBrandLeadInput(
  draft: HireClippersDraft,
  source: string,
): BuildBrandLeadInputResult {
  if (!isOption(DEMAND_TYPE_OPTIONS, draft.demandType)) return fail('demandType', 'required');
  if (!isOption(OBJECTIVE_OPTIONS, draft.objective)) return fail('objective', 'required');
  const contentType = CONTENT_TYPE_OPTIONS.map((option) => option.value).filter(
    (value) => draft.contentType.includes(value),
  );
  if (contentType.length === 0) return fail('contentType', 'required');
  if (!isOption(PLATFORM_OPTIONS, draft.platforms)) return fail('platforms', 'required');
  if (!isOption(BUDGET_OPTIONS, draft.budget)) return fail('budget', 'required');

  const name = draft.name.trim();
  if (name.length < NAME_MIN || name.length > NAME_MAX) return fail('contact', 'invalidName');
  const email = draft.email.trim().toLowerCase();
  if (!EMAIL_RE.test(email) || email.length > EMAIL_MAX) return fail('contact', 'invalidEmail');
  const phone = resolveInternationalMobile({
    country: draft.phoneCountry,
    phone: draft.phone.trim(),
  });
  if (!phone) return fail('contact', 'invalidPhone');
  const company = draft.company.trim().slice(0, COMPANY_MAX);

  return {
    ok: true,
    input: {
      name,
      email,
      phone: phone.e164,
      ...(company ? { company } : {}),
      demand_type: draft.demandType,
      objective: draft.objective,
      content_type: contentType,
      platforms: draft.platforms,
      budget: draft.budget,
      source,
    },
  };
}
